import { FieldState, FormApi as FinalFormApi, setIn } from 'final-form';
import * as t from 'io-ts';
import * as React from 'react';
import * as reactFinalForm from 'react-final-form';
import { ISchema, ValidationError } from 'yup';
import { FlattenObjectKeys, TypeOfNestedKey } from '../types';
export const makeFinalFormValidator =
  <T extends object>(schema: ISchema<T> | t.Type<T>) =>
  async (values: object) => {
    return schema instanceof t.Type
      ? Promise.resolve({})
      : schema
          .validate(values, { abortEarly: false })
          .then((obj) => ({}))
          .catch((e: ValidationError) => {
            return e.inner.reverse().reduce((errors, error) => {
              return setIn(errors, error.path, error.message);
            }, {});
          });
  };

// @ts-expect-error
interface FormApi<
  FormValues extends Record<string | number, any> = Record<
    string | number,
    any
  >,
  InitialFormValues = Partial<FormValues>
> extends Omit<
    FinalFormApi<FormValues, InitialFormValues>,
    'change' | 'focus' | 'getFieldState' | 'resetFieldState'
  > {
  blur: (name: FlattenObjectKeys<FormValues>) => void;
  change: <Name extends FlattenObjectKeys<FormValues>>(
    name: Name,
    value: TypeOfNestedKey<FormValues, Name>
  ) => void;

  focus: (name: FlattenObjectKeys<FormValues>) => void;

  getFieldState: <Name extends FlattenObjectKeys<FormValues>>(
    field: Name
  ) => FieldState<TypeOfNestedKey<FormValues, Name>> | undefined;

  resetFieldState: (name: FlattenObjectKeys<FormValues>) => void;
}

type FieldRenderProps<
  FieldValue,
  T extends HTMLElement = HTMLElement,
  InputValue = FieldValue
> = {
  input: reactFinalForm.FieldInputProps<InputValue, T>;
  meta: reactFinalForm.FieldMetaState<FieldValue>;
};

type FieldProps<
  Form extends Record<string | number, unknown>,
  Name extends FlattenObjectKeys<Form>,
  RP extends FieldRenderProps<TypeOfNestedKey<Form, Name>, T, InputValue>,
  T extends HTMLElement = HTMLElement,
  InputValue = TypeOfNestedKey<Form, Name>
> = reactFinalForm.UseFieldConfig<TypeOfNestedKey<Form, Name>, InputValue> &
  reactFinalForm.RenderableProps<RP> & {
    name: Name;
  };

/** Use this to make a type safe set of react-final-form components and hooks for a given record type  */
export function makeTypedFormHooksAndComponents<
  Form extends Record<string | number, unknown>,
  InitialFormValues = Partial<Form>
>() {
  const useForm = (componentName?: string) =>
    // @ts-expect-error
    reactFinalForm.useForm<Form>(componentName) as FormApi<
      Form,
      InitialFormValues
    >;

  function useField<
    Name extends FlattenObjectKeys<Form>,
    Element extends HTMLElement = HTMLElement,
    InputValue = TypeOfNestedKey<Form, Name>
  >(
    name: Name,
    config?: reactFinalForm.UseFieldConfig<
      TypeOfNestedKey<Form, Name>,
      InputValue
    >
  ) {
    return reactFinalForm.useField<
      TypeOfNestedKey<Form, Name>,
      Element,
      InputValue
    >(name, config);
  }

  const useFormState = reactFinalForm.useFormState<Form, InitialFormValues>;

  const Field = React.memo(function Field<
    Name extends FlattenObjectKeys<Form>,
    Element extends HTMLElement = HTMLElement,
    InputValue = TypeOfNestedKey<Form, Name>
  >(
    props: FieldProps<
      Form,
      Name,
      reactFinalForm.FieldRenderProps<
        TypeOfNestedKey<Form, Name>,
        Element,
        InputValue
      >,
      Element,
      InputValue
    >
  ) {
    // @ts-expect-error
    return React.createElement(reactFinalForm.Field, props);
  });

  const withTypes = reactFinalForm.withTypes<Form, InitialFormValues>();

  return {
    useForm,
    useField,
    useFormState,
    Field,
    Form: withTypes.Form,
    FormSpy: withTypes.FormSpy,
  };
}
